import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { showToast } from "./Toast";
import OtpVerifyModal from "./OtpVerifyModal";
import { isPhoneVerified, checkUserExists, ensureAuthReady } from "../services/otpService";
import { resolveTrackDestination } from "../utils/trackDestination";
import "./AppHeader.css";
import logo from "../assets/turantx-logo.png";

export default function AppHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [overDark, setOverDark] = useState(true);
  // Over the dark hero the header is transparent; once the page scrolls, the
  // hero headline slides up underneath it and the two collide (logo over
  // "SEND."), so it gets a solid navy background from the first pixel of scroll.
  const [pastTop, setPastTop] = useState(false);
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackPhone, setTrackPhone] = useState("");
  const [trackLoading, setTrackLoading] = useState(false);
  const [otpPhone, setOtpPhone] = useState(null); // phone awaiting OTP verification
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = !!localStorage.getItem("PHONE_NUMBER");
  const isDark = location.pathname === "/login" || location.pathname === "/";

  useEffect(() => {
    setOverDark(true);

    if (isDark) {
      // Use IntersectionObserver so GSAP-animated pages are handled correctly
      const tryObserve = () => {
        const darkSection = document.querySelector('[data-theme="dark"]');
        if (!darkSection) {
          // Fallback: scroll-based
          const onScroll = () => setOverDark(window.scrollY < 60);
          window.addEventListener("scroll", onScroll, { passive: true });
          return () => window.removeEventListener("scroll", onScroll);
        }
        const obs = new IntersectionObserver(
          ([entry]) => setOverDark(entry.isIntersecting),
          { rootMargin: "-64px 0px 0px 0px", threshold: 0 }
        );
        obs.observe(darkSection);
        return () => obs.disconnect();
      };

      // Give GSAP / React a tick to mount the dark section
      const tid = setTimeout(() => {
        const cleanup = tryObserve();
        // Store cleanup fn on a ref-like object so we can call it on unmount
        AppHeader._cleanup = cleanup;
      }, 50);

      return () => {
        clearTimeout(tid);
        AppHeader._cleanup?.();
      };
    } else {
      // Non-dark pages: simple scroll detection for any subtle sticky effects
      const onScroll = () => setOverDark(false);
      setOverDark(false);
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }
  }, [isDark, location.pathname]);

  useEffect(() => {
    if (!isDark) return;
    const onScroll = () => setPastTop(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isDark]);

  const handleLogout = async () => {
    try { await signOut(auth); } catch { /* ignore */ } // end the Firebase (OTP) session too
    localStorage.clear();
    sessionStorage.clear();
    setMenuOpen(false);
    navigate("/login", { replace: true });
  };

  const handleBookNow = () => {
    if (location.pathname === "/login" || location.pathname === "/") {
      document.querySelector(".lp-hero")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/login");
    }
  };

  // Rakhi Express bookings live outside the normal Sender/TravelerRequest
  // model — a phone with only a Rakhi booking gets routed to its status page
  // instead of an empty /dashboard. See utils/trackDestination.js.
  const goToDashboard = async (phone) => {
    const dest = await resolveTrackDestination(phone);
    navigate(dest, { state: { phoneNumber: phone } });
  };

  // Track button: if logged in, go straight to own dashboard (OTP only if no
  // verified session). If logged out, open the phone-entry modal.
  const handleTrackClick = async () => {
    setMenuOpen(false);
    if (!isLoggedIn) {
      setTrackOpen(true);
      return;
    }
    const phone = localStorage.getItem("PHONE_NUMBER");
    // Let Auth restore before reading currentUser — see ensureAuthReady().
    // Without this a tap made early in the page's life re-prompts for an OTP
    // the user has already passed.
    await ensureAuthReady();
    if (isPhoneVerified(phone)) goToDashboard(phone);
    else setOtpPhone(phone);
  };

  const handleTrackOrder = async () => {
    const cleaned = trackPhone.replace(/\D/g, "");
    if (cleaned.length !== 10) {
      if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
      showToast("Enter a valid 10-digit mobile number", "warning");
      return;
    }
    setTrackLoading(true);
    try {
      const { exists, hasRakhiBooking } = await checkUserExists(cleaned);
      if (exists || hasRakhiBooking) {
        setTrackOpen(false);
        setTrackPhone("");
        // Require OTP verification before showing the order (unless already verified)
        await ensureAuthReady();
        if (isPhoneVerified(cleaned)) goToDashboard(cleaned);
        else setOtpPhone(cleaned);
      } else {
        showToast("No order found for this number", "error");
      }
    } catch {
      showToast("Something went wrong. Try again.", "error");
    } finally {
      setTrackLoading(false);
    }
  };

  // An open menu always gets the solid white header. Over the dark hero the
  // header is transparent with a WHITE ✕, and the menu panel below is white —
  // so the close icon vanished and users could not find how to close the menu.
  const isTransparent = isDark && overDark && !menuOpen;

  return (
    <>
      <header className={`app-header${isTransparent ? ` app-header--dark${pastTop ? " app-header--dark-solid" : ""}` : " app-header--scrolled"}${menuOpen ? " app-header--menu-open" : ""}`}>
        <Link to="/login" className="header-logo">
          <img
            src={logo}
            alt="TurantX"
            className={isTransparent ? "logo-on-dark" : ""}
          />
        </Link>

        {/* Centre slot — the landing page portals its scrolled promo in here so
            it reads as part of the header chrome instead of a separate
            floating element. Empty (and invisible) on every other page. */}
        <div id="tx-header-promo" className="header-promo-slot" />

        {/* DESKTOP NAV */}
        <nav className="nav-links desktop-only">
          <Link to="/info/about">About</Link>
          <Link to="/demo">How it works</Link>
          <Link to="/info/help">Help</Link>
          <button className="nav-track-btn" onClick={handleTrackClick}>Track</button>
          {isLoggedIn && (
            <button className="logout-link" onClick={handleLogout}>Logout</button>
          )}
          <button
            className={`header-book-btn${isTransparent ? " header-book-btn--hidden" : ""}`}
            onClick={handleBookNow}
            aria-hidden={isTransparent}
          >Book Now</button>
        </nav>

        {/* MOBILE HAMBURGER */}
        <button
          className="hamburger mobile-only"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? "✕" : "☰"}
        </button>
      </header>

      {/* MOBILE MENU */}
      {menuOpen && (
        <div className="mobile-menu">
          <Link onClick={() => setMenuOpen(false)} to="/info/about">About Us</Link>
          <Link onClick={() => setMenuOpen(false)} to="/demo">How it Works</Link>
          <Link onClick={() => setMenuOpen(false)} to="/info/help">Help & Support</Link>
          <button
            className="mobile-track-btn"
            onClick={handleTrackClick}
          >
            Track Order
          </button>
          <Link onClick={() => setMenuOpen(false)} to="/info/privacy">Privacy Policy</Link>
          {isLoggedIn && (
            <button className="mobile-logout-btn" onClick={handleLogout}>Logout</button>
          )}
        </div>
      )}

      {/* TRACK ORDER MODAL */}
      {trackOpen && (
        <div className="track-modal-overlay" onClick={() => setTrackOpen(false)}>
          <div className="track-modal" onClick={(e) => e.stopPropagation()}>
            <button className="track-modal-close" onClick={() => setTrackOpen(false)}>✕</button>
            <span className="track-modal-ping" />
            <h3 className="track-modal-title">Track your order</h3>
            <p className="track-modal-sub">Enter the mobile number used while booking</p>
            <div className="track-modal-input-row">
              <input
                className="track-modal-input clarity-mask"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit mobile number"
                value={trackPhone}
                onChange={(e) => setTrackPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                onKeyDown={(e) => e.key === "Enter" && handleTrackOrder()}
                autoFocus
              />
              <button
                className={`track-modal-btn${trackPhone.length === 10 ? " ready" : ""}`}
                onClick={handleTrackOrder}
                disabled={trackPhone.length !== 10 || trackLoading}
              >
                {trackLoading ? "…" : "→"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP VERIFICATION (track / order access) */}
      {otpPhone && (
        <OtpVerifyModal
          phone={otpPhone}
          title="Verify to view your order"
          onClose={() => setOtpPhone(null)}
          onVerified={() => { const p = otpPhone; setOtpPhone(null); goToDashboard(p); }}
        />
      )}
    </>
  );
}

AppHeader._cleanup = null;
